import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { TrendingUp, Users, BookOpen, Download, Loader2, Award, User, AlertCircle } from 'lucide-react';
import StudentResultDetails from './StudentResultDetails';
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  query,
  where,
  DocumentData,
} from "firebase/firestore";

interface ClassItem {
  id: string;
  name: string;
  classUid: string;
  level: number;
  stream: string;
}

interface ExamType {
  id: string;
  name: string;
  weight: number;
}

interface ExamSession {
  id: string;
  name: string;
  status: "active" | "inactive" | "archived";
  startDate: any;
  endDate: any;
  examTypes: ExamType[];
}

interface Subject {
  subjectId: string;
  name: string;
  code: string;
}

interface StudentResult {
  id: string;
  name: string;
  regNumber: string;
  subjects: {
    [subjectId: string]: {
      subjectName: string;
      marks: number;
      grade: string;
    };
  };
  examTypeScores: {
    [examTypeId: string]: number;
  };
  examTypeSubjectCounts: {
    [examTypeId: string]: number;
  };
  totalMarks: number;
  averageMarks: number;
  overallGrade: string;
  position: number; // Class Position
  overallPosition?: number; // Stream Position (across all streams)
  classUid?: string; // To identify which class they belong to
  hasIncompleteGrades?: boolean;
}

interface GradeBoundary {
  grade: string;
  min: number;
  max: number;
}

interface ProjectedResultsProps {
  schoolID: string;
}

const ProjectedResults = ({ schoolID }: ProjectedResultsProps) => {
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [sessions, setSessions] = useState<ExamSession[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSession, setSelectedSession] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [results, setResults] = useState<StudentResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [gradeBoundaries, setGradeBoundaries] = useState<GradeBoundary[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentResult | null>(null);
  const [view, setView] = useState<'list' | 'details'>('list');
  const [overallTotalStudents, setOverallTotalStudents] = useState<number>(0);

  useEffect(() => {
    if (schoolID && selectedYear) {
      fetchInitialData(schoolID, selectedYear);
    }
  }, [schoolID, selectedYear]);

  // Automatically fetch results when class or session changes
  useEffect(() => {
    if (selectedClass && selectedSession && schoolID) {
      fetchProjectedResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSession, schoolID]);

  const fetchInitialData = async (schoolId: string, year: string) => {
    setLoading(true);
    try {
      // Fetch classes
      const classesCollection = collection(db, `schools/${schoolId}/classes`);
      const classesSnapshot = await getDocs(classesCollection);
      const classesData: ClassItem[] = classesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        classUid: doc.data().classUid || '',
        level: doc.data().level || 0,
        stream: doc.data().stream || '',
      }));
      setClasses(classesData.sort((a, b) => a.name.localeCompare(b.name)));

      // Fetch exam sessions for the selected year
      const sessionsCollection = collection(db, `schools/${schoolId}/examSessions`);
      const sessionsQuery = query(sessionsCollection, where("year", "==", Number(year)));
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessionsData: ExamSession[] = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        status: doc.data().status || 'inactive',
        startDate: doc.data().startDate,
        endDate: doc.data().endDate,
        examTypes: doc.data().examTypes || [],
      }));
      
      // Filter for active sessions (or show all for historical years)
      const filteredSessions = year === new Date().getFullYear().toString() 
        ? sessionsData.filter(s => s.status === 'active')
        : sessionsData;
      setSessions(filteredSessions);

      // Set default session if available
      if (filteredSessions.length > 0) {
        setSelectedSession(filteredSessions[0].id);
      } else {
        setSelectedSession('');
      }

      // Fetch subjects
      const subjectsCollection = collection(db, `schools/${schoolId}/subjects`);
      const subjectsSnapshot = await getDocs(subjectsCollection);
      const subjectsData: Subject[] = subjectsSnapshot.docs.map(doc => ({
        subjectId: doc.id,
        name: doc.data().name || '',
        code: doc.data().code || '',
      }));
      setSubjects(subjectsData);

      // Fetch active grading scheme
      const schemesCollection = collection(db, `schools/${schoolId}/gradingSchemes`);
      const schemesSnapshot = await getDocs(schemesCollection);
      const activeScheme = schemesSnapshot.docs.find(doc => doc.data().isActive);
      if (activeScheme) {
        setGradeBoundaries(activeScheme.data().boundaries || []);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast({
        title: "Error",
        description: "Failed to load data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateGrade = (marks: number): string => {
    if (!gradeBoundaries.length) return 'N/A';
    
    const roundedMarks = Math.round(marks);
    const sortedBoundaries = [...gradeBoundaries].sort((a, b) => b.min - a.min);
    
    for (const boundary of sortedBoundaries) {
      if (roundedMarks >= boundary.min && roundedMarks <= boundary.max) {
        return boundary.grade;
      }
    }
    
    // If no exact match, find the closest grade
    if (roundedMarks > 100) return sortedBoundaries[0]?.grade || 'N/A';
    
    // Fallback for gaps: Find the grade where roundedMarks >= min
    if (roundedMarks >= 0) {
      for (const boundary of sortedBoundaries) {
        if (roundedMarks >= boundary.min) {
          return boundary.grade;
        }
      }
    }
    
    return 'E';
  };

  const fetchProjectedResults = async () => {
    if (!selectedClass || !selectedSession || !schoolID) {
      toast({
        title: "Selection Required",
        description: "Please select both a class and session.",
        variant: "destructive",
      });
      return;
    }

      setLoadingResults(true);
    try {
      // Get the selected class details
      const selectedClassData = classes.find(c => c.id === selectedClass);
      if (!selectedClassData) return;

      // Find all classes in the same grade level (for Stream Ranking)
      // If selectedClassData.level is defined, use it. Otherwise just use selected class.
      const peerClasses = (selectedClassData.level && selectedClassData.level !== 0)
        ? classes.filter(c => c.level === selectedClassData.level)
        : [selectedClassData];
      
      console.log(`[Result Projector] Fetching stream context for ${selectedClassData.name} (Level ${selectedClassData.level})`);
      console.log(`[Result Projector] Peer classes found: ${peerClasses.length}`, peerClasses.map(c => c.name));

      const peerClassUids = peerClasses.map(c => c.classUid).filter(Boolean);

      // Fetch students in ALL peer classes
      const studentsCollection = collection(db, `schools/${schoolID}/students`);
      
      // Firestore 'in' query supports max 10 items. We must batch if there are more streams.
      let allStudentDocs: any[] = [];
      const chunks = [];
      for (let i = 0; i < peerClassUids.length; i += 10) {
        chunks.push(peerClassUids.slice(i, i + 10));
      }

      if (chunks.length === 0) {
         // Fallback if no valid UIDs (shouldn't happen if selected class has UID)
         const q = query(studentsCollection, where("currentClassUid", "==", selectedClassData.classUid));
         const snap = await getDocs(q);
         allStudentDocs = snap.docs;
      } else {
         await Promise.all(chunks.map(async (chunk) => {
            const q = query(studentsCollection, where("currentClassUid", "in", chunk));
            const snap = await getDocs(q);
            // Safe array spread
            allStudentDocs = [...allStudentDocs, ...snap.docs];
         }));
      }

      // De-duplicate students (just in case of overlapping queries - unlikely but safe)
      const uniqueStudents = new Map();
      allStudentDocs.forEach(doc => uniqueStudents.set(doc.id, doc));
      
      const studentIds = Array.from(uniqueStudents.keys());
      const studentsMap = new Map(
        Array.from(uniqueStudents.values()).map((doc: any) => {
          const data = doc.data();
          return [
            doc.id,
            {
              name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
              regNumber: data.registrationNumber || data.admissionNumber || 'N/A',
              enrolledSubjects: data.enrolledSubjects || [],
              classUid: data.currentClassUid // Capture classUid for filtering
            }
          ];
        })
      );

      if (studentIds.length === 0) {
        setResults([]);
        toast({
          title: "No Students",
          description: "No students found in this class.",
        });
        return;
      }

      // Fetch all grades for these students in the selected session
      const gradesCollection = collection(db, `schools/${schoolID}/grades`);

      let allGrades: DocumentData[] = [];
      // Fetch in batches due to Firestore's 'in' query limit of 10
      for (let i = 0; i < studentIds.length; i += 10) {
        const batch = studentIds.slice(i, i + 10);
        // We fetch all grades for the session and filter in memory to be safe about 'isDraft' field existence
        const batchQuery = query(
          gradesCollection,
          where("studentId", "in", batch),
          where("examSessionId", "==", selectedSession)
        );
        const batchSnapshot = await getDocs(batchQuery);
        allGrades = [...allGrades, ...batchSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))];
      }

      // Build the valid category set for the selected session.
      // When categories are updated, old grade docs still exist but point to old categoryIds.
      // For each subject+examType, if a category set exists for this session, ONLY those categoryIds are considered valid.
      const categoriesCollection = collection(db, `schools/${schoolID}/examCategories`);
      const sessionCategoriesQuery = query(categoriesCollection, where("examSessionId", "==", selectedSession));
      const sessionCategoriesSnapshot = await getDocs(sessionCategoriesQuery);
      const validCategoryIdsBySubjectExamType = new Map<string, Set<string>>();
      sessionCategoriesSnapshot.docs.forEach((docSnap) => {
        const data: any = docSnap.data();
        const subjectId = data.subjectId;
        const examTypeId = data.examTypeId;
        if (!subjectId || !examTypeId) return;
        const key = `${subjectId}_${examTypeId}`;
        if (!validCategoryIdsBySubjectExamType.has(key)) {
          validCategoryIdsBySubjectExamType.set(key, new Set());
        }
        validCategoryIdsBySubjectExamType.get(key)!.add(docSnap.id);
      });

      // If a subject+examType has a configured category set for this session, drop grades tied to older categoryIds.
      allGrades = allGrades.filter((g: any) => {
        const subjectId = g.subjectId;
        const examTypeId = g.examTypeId;
        const categoryId = g.categoryId;
        if (!subjectId || !examTypeId) return true;
        const key = `${subjectId}_${examTypeId}`;
        const validSet = validCategoryIdsBySubjectExamType.get(key);
        // No configured categories -> don't filter (keeps legacy/other subjects visible)
        if (!validSet || validSet.size === 0) return true;
        // Configured categories exist -> require categoryId to be in the set
        if (!categoryId) return false;
        return validSet.has(categoryId);
      });

      // Deduplicate grades: when categories are updated or teachers save multiple times,
      // it is possible to have multiple grade docs for the same student+subject+examType+category.
      // For reporting we keep ONE: prefer submitted over draft, then the most recent timestamp.
      const getMillis = (ts: any): number => {
        try {
          return typeof ts?.toMillis === 'function' ? ts.toMillis() : 0;
        } catch {
          return 0;
        }
      };
      const getBestTimestamp = (g: any): number => (
        getMillis(g.submittedAt) ||
        getMillis(g.updatedAt) ||
        getMillis(g.createdAt) ||
        0
      );
      const isSubmitted = (g: any): boolean => g.isDraft !== true;
      const bestByKey = new Map<string, any>();
      for (const g of allGrades) {
        const examTypeId = g.examTypeId || 'default';
        // Prefer categoryId now that we filtered to the current category set.
        // Fallback to categoryName for legacy docs.
        const categoryKey = g.categoryId || (((g.categoryName ?? '') as string).trim().toLowerCase() || 'default');
        const key = `${g.studentId}_${g.subjectId}_${examTypeId}_${categoryKey}`;
        const existing = bestByKey.get(key);
        if (!existing) {
          bestByKey.set(key, g);
          continue;
        }
        // Prefer submitted over draft
        if (isSubmitted(g) && !isSubmitted(existing)) {
          bestByKey.set(key, g);
          continue;
        }
        if (isSubmitted(g) === isSubmitted(existing) && getBestTimestamp(g) > getBestTimestamp(existing)) {
          bestByKey.set(key, g);
        }
      }
      allGrades = Array.from(bestByKey.values());

      // Organize grades by student
      const studentResultsMap = new Map<string, StudentResult>();

      studentIds.forEach(studentId => {
        const studentInfo = studentsMap.get(studentId);
        if (studentInfo) {
          studentResultsMap.set(studentId, {
            id: studentId,
            name: studentInfo.name,
            regNumber: studentInfo.regNumber,
            classUid: studentInfo.classUid,
            subjects: {},
            examTypeScores: {},
            examTypeSubjectCounts: {},
            totalMarks: 0,
            averageMarks: 0,
            overallGrade: '',
            position: 0,
            overallPosition: 0,
            hasIncompleteGrades: false,
          });
        }
      });

      // Populate subject marks and exam type scores
      allGrades.forEach(grade => {
        const studentResult = studentResultsMap.get(grade.studentId);
        
        // Use normalizedExamScore if available, otherwise fallback to marks
        // normalizedExamScore is already weighted by the category weight and exam type weight
        // e.g. (Marks / Max) * (CategoryWeight / 100) * ExamTypeWeight
        const score = grade.normalizedExamScore !== undefined ? grade.normalizedExamScore : (grade.marks || 0);
        
        if (studentResult && score !== null) {
          const subject = subjects.find(s => s.subjectId === grade.subjectId);
          if (subject) {
            if (!studentResult.subjects[grade.subjectId]) {
              studentResult.subjects[grade.subjectId] = {
                subjectName: subject.name,
                marks: 0,
                grade: 'N/A',
              };
            }
            // Accumulate scores from different exam categories (e.g. Paper 1 + Paper 2)
            // Since normalizedExamScore is already weighted, we just sum them up
            // However, we need to be careful not to double count if multiple entries exist for same category (shouldn't happen if unique)
            // But here we are iterating over all grades.
            // Wait, if we have multiple categories for the SAME exam type, we sum them up.
            // e.g. Entry Exam (20%) -> Paper 1 (40%), Paper 2 (40%), Paper 3 (20%)
            // Score 1: (60/80) * 0.4 * 20 = 6
            // Score 2: (30/80) * 0.4 * 20 = 3
            // Score 3: (30/40) * 0.2 * 20 = 3
            // Total for Entry Exam = 6 + 3 + 3 = 12. Correct.
            
            // The issue might be that we are just adding blindly.
            // We need to make sure we are adding to the correct subject.
            studentResult.subjects[grade.subjectId].marks += score;
          }

          // Aggregate scores by exam type (track per subject to calculate average later)
          if (grade.examTypeId) {
            const key = `${grade.examTypeId}_${grade.subjectId}`;
            // Track unique subject contributions to avoid double counting
            if (!studentResult.examTypeScores[key]) {
              studentResult.examTypeScores[key] = 0;
            }
            studentResult.examTypeScores[key] += score;
          }
        }
      });

      // Calculate average exam type scores across subjects
      studentResultsMap.forEach(student => {
        const examTypeAverages: { [examTypeId: string]: number } = {};
        const examTypeCounts: { [examTypeId: string]: Set<string> } = {};
        
        // Group by exam type and count unique subjects
        Object.keys(student.examTypeScores).forEach(key => {
          const [examTypeId, subjectId] = key.split('_');
          if (!examTypeCounts[examTypeId]) {
            examTypeCounts[examTypeId] = new Set();
            examTypeAverages[examTypeId] = 0;
          }
          examTypeCounts[examTypeId].add(subjectId);
          examTypeAverages[examTypeId] += student.examTypeScores[key];
        });
        
        // Calculate averages
        const finalExamTypeScores: { [examTypeId: string]: number } = {};
        Object.keys(examTypeAverages).forEach(examTypeId => {
          const count = examTypeCounts[examTypeId].size;
          finalExamTypeScores[examTypeId] = count > 0 ? examTypeAverages[examTypeId] / count : 0;
        });
        
        student.examTypeScores = finalExamTypeScores;
      });

      // Calculate grades for each subject after summing
      studentResultsMap.forEach(student => {
        Object.values(student.subjects).forEach(subject => {
          subject.grade = calculateGrade(subject.marks);
        });
        
        // Get student's enrolled subjects
        const studentInfo = studentsMap.get(student.id);
        const enrolledSubjects = studentInfo?.enrolledSubjects || [];
        
        // Build expected categories for THIS student's enrolled subjects
        const expectedCategoriesForStudent = new Set<string>();
        allGrades.forEach(grade => {
          // Only include categories for subjects this student is enrolled in
          if (enrolledSubjects.includes(grade.subjectId)) {
            const key = `${grade.subjectId}_${grade.examTypeId}_${grade.categoryId || 'default'}`;
            expectedCategoriesForStudent.add(key);
          }
        });
        
        // Build student's actual categories
        const studentCategories = new Set<string>();
        allGrades.forEach(grade => {
          if (grade.studentId === student.id) {
            const key = `${grade.subjectId}_${grade.examTypeId}_${grade.categoryId || 'default'}`;
            studentCategories.add(key);
          }
        });
        
        // If student is missing any categories for their enrolled subjects, mark as incomplete
        if (studentCategories.size < expectedCategoriesForStudent.size) {
          student.hasIncompleteGrades = true;
        }
      });

      // Calculate totals and averages
      const resultsArray: StudentResult[] = Array.from(studentResultsMap.values()).map(student => {
        const subjectMarks = Object.values(student.subjects).map(s => s.marks);
        const totalMarks = subjectMarks.reduce((sum, marks) => sum + marks, 0);
        // Average should be based on number of subjects taken, or total subjects?
        // Usually average is total marks / number of subjects
        const averageMarks = subjectMarks.length > 0 ? totalMarks / subjectMarks.length : 0;
        const overallGrade = calculateGrade(averageMarks);

        return {
          ...student,
          totalMarks,
          averageMarks,
          overallGrade,
        };
      });

      // 1. Sort ALL students by average marks (decending) for Overall/Stream Position ranking
      resultsArray.sort((a, b) => b.averageMarks - a.averageMarks);
      
      // 2. Assign overall positions (Rank across all streams in the grade)
      resultsArray.forEach((student, index) => {
        student.overallPosition = index + 1;
      });

      // 3. Store total number of students in the grade
      setOverallTotalStudents(resultsArray.length);

      // 4. Filter for selected class ONLY
      // Logic: Only show students whose currentClassUid matches the selected class UID
      const classResults = resultsArray.filter(student => student.classUid === selectedClassData.classUid);

      // 5. Sort Class Results by average for Class Position ranking
      classResults.sort((a, b) => b.averageMarks - a.averageMarks);

      // 6. Assign Class Positions
      classResults.forEach((student, index) => {
        student.position = index + 1; // Class Position
      });

      setResults(classResults);

      if (resultsArray.length === 0) {
        toast({
          title: "No Results",
          description: "No submitted grades found for this class and term.",
        });
      }
    } catch (error) {
      console.error("Error fetching projected results:", error);
      toast({
        title: "Error",
        description: "Failed to load projected results.",
        variant: "destructive",
      });
    } finally {
      setLoadingResults(false);
    }
  };

  const exportResults = () => {
    if (results.length === 0) {
      toast({
        title: "No Data",
        description: "No results to export.",
        variant: "destructive",
      });
      return;
    }

    const selectedClassName = classes.find(c => c.id === selectedClass)?.name || 'Unknown Class';
    const selectedSessionName = sessions.find(s => s.id === selectedSession)?.name || 'Unknown Session';

    // Create CSV content
    let csv = `Projected Results - ${selectedClassName} - ${selectedSessionName}\n\n`;
    csv += 'Position,Reg Number,Student Name,';
    
    // Add subject headers
    const subjectIds = Object.keys(results[0]?.subjects || {});
    subjectIds.forEach(subjectId => {
      const subject = subjects.find(s => s.subjectId === subjectId);
      csv += `${subject?.name || subjectId},`;
    });
    csv += 'Total,Average,Grade\n';

    // Add student data
    results.forEach(student => {
      csv += `${student.position},${student.regNumber},"${student.name}",`;
      subjectIds.forEach(subjectId => {
        csv += `${student.subjects[subjectId]?.marks || '-'},`;
      });
      csv += `${student.totalMarks.toFixed(1)},${student.averageMarks.toFixed(1)},${student.overallGrade}\n`;
    });

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedClassName}_${selectedSessionName}_Projected_Results.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: "Results exported successfully.",
    });
  };

  const getGradeColor = (grade: string) => {
    const upperGrade = grade.toUpperCase();
    if (upperGrade.startsWith('A')) return 'bg-green-500';
    if (upperGrade.startsWith('B')) return 'bg-blue-500';
    if (upperGrade.startsWith('C')) return 'bg-yellow-500';
    if (upperGrade.startsWith('D')) return 'bg-orange-500';
    if (upperGrade.startsWith('E')) return 'bg-red-500';
    return 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 flex-1 w-full flex flex-col">
      {view === 'list' && (
        <>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 m-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="h-7 w-7" />
            Projected Results
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            View comprehensive results analysis by class and term
          </p>
        </div>
      </div>

      <Card className="border border-gray-200 dark:border-[#1E293B] bg-white dark:bg-[#020817] m-2">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
            Select Class and Term
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Academic Year
              </label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] max-h-[300px]">
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <SelectItem 
                        key={year} 
                        value={year.toString()}
                        className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31]"
                      >
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Class
              </label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]">
                  <SelectValue placeholder="Select Class" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] max-h-[300px]">
                  {classes.map(cls => (
                    <SelectItem 
                      key={cls.id} 
                      value={cls.id}
                      className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31]"
                    >
                      {cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Term / Session
              </label>
              <Select value={selectedSession} onValueChange={setSelectedSession}>
                <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]">
                  <SelectValue placeholder="Select Session" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] max-h-[300px]">
                  {sessions.map(session => (
                    <SelectItem 
                      key={session.id} 
                      value={session.id}
                      className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31]"
                    >
                      {session.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border border-gray-200 dark:border-[#1E293B] bg-white dark:bg-[#020817] m-2">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Students</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{results.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 dark:border-[#1E293B] bg-white dark:bg-[#020817] m-2">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Award className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Class Average</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {(results.reduce((sum, r) => sum + r.averageMarks, 0) / results.length).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 dark:border-[#1E293B] bg-white dark:bg-[#020817] m-2">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <BookOpen className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Top Score</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {results[0]?.averageMarks.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 dark:border-[#1E293B] bg-white dark:bg-[#020817] m-2">
              <CardContent className="pt-6">
                <Button 
                  onClick={exportResults}
                  variant="outline"
                  className="w-full h-full min-h-[80px] flex-col gap-2"
                >
                  <Download className="h-6 w-6" />
                  <span className="text-sm font-semibold">Export Results</span>
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-gray-200 dark:border-[#1E293B] bg-white dark:bg-[#020817] m-2 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-[#1E293B]">
                    <TableHead className="w-[100px] font-semibold text-gray-900 dark:text-white">Position</TableHead>
                    <TableHead className="font-semibold text-gray-900 dark:text-white">Student Name</TableHead>
                    <TableHead className="font-semibold text-gray-900 dark:text-white">Reg Number</TableHead>
                    {sessions.find(s => s.id === selectedSession)?.examTypes.map(et => (
                      <TableHead key={et.id} className="text-center font-semibold text-gray-900 dark:text-white">
                        {et.name} ({et.weight}%)
                      </TableHead>
                    ))}
                    <TableHead className="text-center font-semibold text-gray-900 dark:text-white">Total Marks</TableHead>
                    <TableHead className="text-center font-semibold text-gray-900 dark:text-white">Average</TableHead>
                    <TableHead className="text-center font-semibold text-gray-900 dark:text-white">Grade</TableHead>
                    <TableHead className="text-right font-semibold text-gray-900 dark:text-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((student) => (
                    <TableRow 
                      key={student.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-[#1E293B] transition-colors"
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-bold text-sm">
                          {student.position}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                            <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">{student.name}</span>
                            {student.hasIncompleteGrades && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertCircle className="h-4 w-4 text-orange-500 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Some of your marks weren't submitted or missing</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500 dark:text-gray-400">
                        {student.regNumber}
                      </TableCell>
                      {sessions.find(s => s.id === selectedSession)?.examTypes.map(et => (
                        <TableCell key={et.id} className="text-center font-medium text-gray-900 dark:text-white">
                          {student.examTypeScores[et.id] !== undefined 
                            ? student.examTypeScores[et.id].toFixed(1) 
                            : '-'}
                        </TableCell>
                      ))}
                      <TableCell className="text-center font-medium text-gray-900 dark:text-white">
                        {student.totalMarks.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-center font-medium text-gray-900 dark:text-white">
                        {student.averageMarks.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${getGradeColor(student.overallGrade)} text-white hover:opacity-90`}>
                          {student.overallGrade}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedStudent(student);
                            setView('details');
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      {!loadingResults && results.length === 0 && selectedClass && selectedSession && (
        <Card className="border border-gray-200 dark:border-[#1E293B] bg-white dark:bg-[#020817] m-2">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <TrendingUp className="h-16 w-16 mx-auto mb-4 opacity-50 text-gray-400" />
              <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No Results Available
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click "View Results" to load projected results for the selected class and term.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}

      {view === 'details' && selectedStudent && (
        <StudentResultDetails
          student={{
            id: selectedStudent.id,
            name: selectedStudent.name,
            regNumber: selectedStudent.regNumber
          }}
          session={{
            id: selectedSession,
            name: sessions.find(s => s.id === selectedSession)?.name || 'Unknown Session'
          }}
          schoolID={schoolID}
          position={selectedStudent.position}
          totalStudents={results.length}
          overallPosition={selectedStudent.overallPosition}
          overallTotal={overallTotalStudents}
          allowEditing={true}
          showAiActions={true}
          onBack={() => {
            setView('list');
            setSelectedStudent(null);
          }}
        />
      )}
    </div>
  );
};

export default ProjectedResults;
